import requests from './requests.json'

const state = {
  snackbar: false,
  invoiceStatusColor: {
    paid: 'success',
    late: 'error',
    pending: 'primary',
    canceled: 'light-gray',
    update: 'secondary',
    cancel: 'secondary',
    create: 'secondary',
    synchronize: 'light-gray',
    renegotiated: 'light-gray',
    default: 'light-gray'
  },
  updatSystemModalOpen: false,
  requests: requests.sort((a, b) => {
    if (a.title < b.title) return -1
    if (a.title > b.title) return 1
    return 0
  }),
  transfers: [],
  transfersLastUpdate: null,
  transfersLoadedAccountId: null,
  transfersLoadedCategories: []
}

// Getters
const getters = {
  // UserData
  snackbar (state) {
    return state.snackbar
  },
  invoiceStatusColor: (state) => (status) => {
    return (state.invoiceStatusColor[status]) ? state.invoiceStatusColor[status] : state.invoiceStatusColor.default
  },
  updatSystemModalOpen (state) {
    return state.updatSystemModalOpen
  },
  requests (state) {
    return state.requests
  },
  requestById: (state) => (id) => {
    const request = state.requests.find(e => e.id === id)
    return request || {}
  },
  transfers: (state) => (accountId) => {
    // Dados da API já vêm filtrados por account_id, filtra só por category
    return state.transfers.filter(e => e.category === 'transfer' && (!e.account_id || e.account_id === accountId))
  },
  withdraws: (state) => (accountId) => {
    return state.transfers.filter(e => e.category === 'request_withdraw' && (!e.account_id || e.account_id === accountId))
  },
  heppiPlan: (state) => (accountId) => {
    return state.transfers.filter(e => e.category === 'heppi_plan' && (!e.account_id || e.account_id === accountId))
  },
  cashback: (state) => (accountId) => {
    return state.transfers.filter(e => e.category === 'cashback' && (!e.account_id || e.account_id === accountId))
  },
  transactionById: (state) => (transactionId) => {
    return state.transfers.find(e => e._id.$oid === transactionId) || null
  },
  transfersLastUpdate (state) {
    return state.transfersLastUpdate
  }
}

// Mutations
const mutations = {
  'SET_SNACKBAR' (state, snackbar) {
    state.snackbar = snackbar
  },
  'SET_UPDATE_SYSTEM_MODAL' (state, modalStatus) {
    state.updatSystemModalOpen = modalStatus
  },
  'SET_TRANSFERS' (state, { transfers, accountId, category }) {
    // Normaliza formato: API retorna campos simples, código espera formato MongoDB ($oid, $date)
    const normalized = transfers.map(e => {
      const item = { ...e }
      if (typeof item._id === 'string') {
        item._id = { $oid: item._id }
      }
      if (typeof item.payment_date === 'string') {
        item.payment_date = { $date: item.payment_date }
      }
      if (typeof item.financial_date === 'string') {
        item.financial_date = { $date: item.financial_date }
      }
      if (typeof item.due_date === 'string') {
        item.due_date = { $date: item.due_date }
      }
      if (typeof item.created_at === 'string') {
        item.created_at = { $date: item.created_at }
      }
      if (typeof item.updated_at === 'string') {
        item.updated_at = { $date: item.updated_at }
      }
      if (typeof item.money_release_date === 'string') {
        item.money_release_date = { $date: item.money_release_date }
      }
      return item
    })
    const transfersToIgnore = []
    for (const index in normalized) {
      const transfer = normalized[index]
      if (transfer.transferRefunded) {
        transfersToIgnore.push(transfer.transferRefunded)
        transfersToIgnore.push(transfer._id.$oid)
      }
    }
    const newItems = normalized
      .filter(e => !transfersToIgnore.includes(e._id.$oid))
      .sort((a, b) => {
        if (a._id.$oid > b._id.$oid) return 1
        if (b._id.$oid > a._id.$oid) return -1
        return 0
      })

    // Se mudou de account, limpa tudo
    if (state.transfersLoadedAccountId !== accountId) {
      state.transfers = newItems
      state.transfersLoadedCategories = category ? [category] : []
    } else {
      // Mesmo account: merge — remove itens da mesma category e adiciona os novos
      if (category) {
        state.transfers = state.transfers.filter(e => e.category !== category).concat(newItems)
        if (!state.transfersLoadedCategories.includes(category)) {
          state.transfersLoadedCategories.push(category)
        }
      } else {
        state.transfers = newItems
        state.transfersLoadedCategories = []
      }
    }

    state.transfersLastUpdate = new Date()
    state.transfersLoadedAccountId = accountId
  }
}

// Actions
const actions = {
  fetchTransfers ({ commit, state }, { accountId, category }) {
    // Cache: se mesmo account e category já carregada, não busca de novo
    if (state.transfersLoadedAccountId === accountId) {
      if (!category || state.transfersLoadedCategories.includes(category)) {
        return Promise.resolve()
      }
    }
    const body = { accountId }
    if (category) {
      body.category = category
    }
    return fetch('https://n8n.heppi.io/webhook/mongodb-payments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(response => {
        if (response.ok) return response.json()
        throw new Error('Não foi possível buscar as transações')
      })
      .then(data => {
        let transfers = Array.isArray(data) ? data : [data]
        if (transfers.length > 0 && transfers[0].json) {
          transfers = transfers.map(e => e.json)
        }
        commit('SET_TRANSFERS', { transfers, accountId, category })
      })
      .catch(error => {
        console.error('[fetchTransfers] Erro:', error)
      })
  },
  copyToClipboard ({ commit }, value) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value)
        .then(() => {
          commit('SET_SNACKBAR', { text: 'Código copiado!' })
        }, () => {
          commit('SET_SNACKBAR', { text: 'Não foi possivel copiar o código' })
        })
    } else {
      commit('SET_SNACKBAR', { text: 'Não foi possivel copiar o código' })
    }
  },
  // eslint-disable-next-line
  clubhouseApi ({}, infos) {
    return new Promise((resolve, reject) => {
      if (!infos.url) {
        reject(new Error('Precisa da url'))
      } else {
        let testURL = (infos.isBlob)
          ? infos.url + '?token=6096a037-1893-4d44-aaae-e514b6d9cb52'
          : `https://api.clubhouse.io/api/v3/${infos.url}?token=6096a037-1893-4d44-aaae-e514b6d9cb52`
        if (infos.params) {
          for (const key in infos.params) {
            testURL += `&${key}=${infos.params[key]}`
          }
        }
        const myInit = (infos.isBlob) ? {
          method: infos.method || 'GET',
          mode: 'no-cors'
        } : {
          method: infos.method || 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json'
          }
        }
        if (infos.body) {
          if (infos.body instanceof FormData) {
            myInit.body = infos.body
          } else {
            myInit.headers['Content-type'] = 'application/json'
            myInit.body = JSON.stringify(infos.body)
          }
        }

        fetch(testURL, myInit).then(response => {
          console.log(response)
          if (infos.isBlob) {
            console.log(response)
            response.blob()
              .then(blob => resolve(blob))
          } else if (response.ok) {
            response.json()
              .then(json => resolve(json))
              .catch(() => {
                if (response.status === 204) resolve(true)
                else reject(new Error('Não foi possivel trazer as informações'))
              })
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
      }
    })
  },
  // eslint-disable-next-line
  asanaApi ({}, infos) {
    return new Promise((resolve, reject) => {
      if (!infos.url) {
        reject(new Error('Precisa da url'))
      } else {
        let testURL = `https://app.asana.com/api/1.0/${infos.url}`
        if (infos.params) {
          testURL += '?'
          for (const key in infos.params) {
            testURL += `${key}=${infos.params[key]}&`
          }
          testURL = testURL.substring(0, testURL.length - 1)
        }
        const myInit = {
          method: infos.method || 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer 2/1207534226710033/1208514373196968:390ec52d615c76f7b52937c1a4f1690b'
          }
        }
        if (infos.body) {
          if (infos.body instanceof FormData) {
            myInit.body = infos.body
          } else {
            myInit.headers['Content-type'] = 'application/json'
            myInit.body = JSON.stringify(infos.body)
          }
        }

        fetch(testURL, myInit).then(response => {
          if (response.ok) {
            response.json()
              .then(json => resolve(json))
              .catch(() => {
                if (response.status === 204) resolve(true)
                else reject(new Error('Não foi possivel trazer as informações'))
              })
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
      }
    })
  },
  // eslint-disable-next-line
  huggyApi ({}, infos) {
    return new Promise((resolve, reject) => {
      if (!infos.url) {
        reject(new Error('Precisa da url'))
      } else {
        let testURL = `https://api.huggy.app/v2/${infos.url}`
        if (infos.params) {
          testURL += '?'
          for (const key in infos.params) {
            testURL += `${key}=${infos.params[key]}&`
          }
          testURL = testURL.substring(0, testURL.length - 1)
        }
        const myInit = {
          method: infos.method || 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'X-Authorization': `Bearer ${process.env.VUE_APP_HUGGY_V2_TOKEN}`
          }
        }
        if (infos.body) {
          if (infos.body instanceof FormData) {
            myInit.body = infos.body
          } else {
            myInit.headers['Content-type'] = 'application/json'
            myInit.body = JSON.stringify(infos.body)
          }
        }

        fetch(testURL, myInit).then(response => {
          if (response.ok) {
            response.json()
              .then(json => resolve(json))
              .catch(() => {
                if (response.status === 204) resolve(true)
                else reject(new Error('Não foi possivel trazer as informações'))
              })
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
      }
    })
  },
  // eslint-disable-next-line
  firebaseApi ({}, endpoint) {
    return new Promise((resolve, reject) => {
      if (!endpoint) {
        reject(new Error('Precisa do endpoint'))
      } else {
        let url = `https://us-central1-heppi-production.cloudfunctions.net/${endpoint}`
        // let url = `http://localhost:5001/heppi-production/us-central1/${endpoint}`
        const myInit = {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json'
          }
        }

        fetch(url, myInit).then(response => {
          if (response.ok) {
            response.json()
              .then(json => resolve(json))
              .catch(() => {
                if (response.status === 204) resolve(true)
                else reject(new Error('Não foi possivel trazer as informações'))
              })
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
      }
    })
  },
  // eslint-disable-next-line
  n8nApi ({}, infos) {
    return new Promise((resolve, reject) => {
      if (!infos.url) {
        reject(new Error('Precisa da url'))
      } else {
        let testURL = `https://n8n.heppi.io/webhook/${infos.url}`
        if (infos.params) {
          testURL += '?'
          for (const key in infos.params) {
            testURL += `${key}=${infos.params[key]}&`
          }
          testURL = testURL.substring(0, testURL.length - 2)
        }
        const myInit = {
          method: infos.method || 'GET',
          headers: {
            'Accept': 'application/json'
          }
        }
        if (infos.body) {
          if (infos.body instanceof FormData) {
            myInit.body = infos.body
          } else {
            myInit.headers['Content-type'] = 'application/json'
            myInit.body = JSON.stringify(infos.body)
          }
        }

        fetch(testURL, myInit).then(async response => {
          if (response.ok) {
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
              const data = await response.json()
              if (data && data.length > 0) {
                resolve(data)
              } else {
                resolve(true)
              }
            } else {
              const data = await response.blob()
              resolve(data)
            }
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
      }
    })
  },
  sendWhatsapp ({}, params) {
    return new Promise((resolve, reject) => {
      // const urlParams = {
      //   source: '558181812327',
      //   destination: params.destination,
      //   template: `{"id":"${params.template}","params":${JSON.stringify(params.params)}}`,
      //   'src.name': 'heppidesenvolvimento',
      //   channel: 'whatsapp'
      // }
      // console.log(urlParams)
      // var formBody = []
      // for (var property in urlParams) {
      //   var encodedKey = encodeURIComponent(property)
      //   var encodedValue = encodeURIComponent(urlParams[property])
      //   formBody.push(encodedKey + '=' + encodedValue)
      // }
      // formBody = formBody.join('&')

      fetch('https://n8n.heppi.io/webhook/d92acb96-c51e-4825-9901-00d7a884c97f', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'rgmvrgmrtolbhktohperewwe'
        },
        body: JSON.stringify(params)
      })
        .then(response => {
          if (response.ok) {
            resolve(true)
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
      // const urlParams = {
      //   source: '558181812327',
      //   destination: params.destination,
      //   template: `{"id":"${params.template}","params":${JSON.stringify(params.params)}}`,
      //   'src.name': 'heppidesenvolvimento',
      //   channel: 'whatsapp'
      // }
      // console.log(urlParams)
      // var formBody = []
      // for (var property in urlParams) {
      //   var encodedKey = encodeURIComponent(property)
      //   var encodedValue = encodeURIComponent(urlParams[property])
      //   formBody.push(encodedKey + '=' + encodedValue)
      // }
      // formBody = formBody.join('&')

      // fetch('https://api.gupshup.io/wa/api/v1/template/msg', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      //     'apikey': 'swqqf1xsnydbvy5n9xgkzcuqjzrxf0zg'
      //   },
      //   body: formBody
      // })
      //   .then(response => {
      //     if (response.ok) {
      //       resolve(true)
      //     } else {
      //       reject(new Error('Não foi possivel trazer as informações'))
      //     }
      //   })
    })
  },
  sendEmail ({}, params) {
    return new Promise((resolve, reject) => {
      fetch('https://n8n.heppi.io/webhook/455ad5c1-f3b1-4ad2-a9ac-1f2f09e9af7d', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'rgmvrgmrtolbhktohperewwe'
        },
        body: JSON.stringify(params)
      })
        .then(response => {
          if (response.ok) {
            resolve(true)
          } else {
            reject(new Error('Não foi possivel trazer as informações'))
          }
        })
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
